/**
 * Closed lists, the hook radio, and reset have no draft. A failed save leaves
 * the screen on the server value, so keeping the failure would block Share
 * after the brief has already snapped back.
 */
const BRIEF_SAVE_FIELDS_WITHOUT_DRAFT = new Set([
  "duration",
  "hookPackage",
  "ageRange",
  "gender",
  "selectedHookIndex",
  "resetBrand",
]);

export function shouldRetainBriefSaveFailure(field: string): boolean {
  return !BRIEF_SAVE_FIELDS_WITHOUT_DRAFT.has(field);
}

export interface PendingBriefSaveTrackOptions {
  /**
   * When false, a settled failure is dropped. A Share already waiting on that
   * request still sees the failure.
   */
  retainFailure?: boolean;
}

/**
 * Tracks each brief field's latest save so Share can wait for it.
 * A failed field stays failed until that field saves successfully, the edit
 * is dropped and `clearFailure` runs, or `retainFailure` is false.
 */
export function createPendingBriefSave() {
  const latest = new Map<string, Promise<boolean>>();
  const failed = new Set<string>();

  return {
    track(field: string, job: Promise<boolean>, options?: PendingBriefSaveTrackOptions): void {
      const retainFailure = options?.retainFailure !== false;
      failed.delete(field);
      const settled = job.then(
        (ok) => ok,
        () => false,
      );
      const recorded = settled.then((ok) => {
        if (latest.get(field) !== recorded) return ok;
        if (ok || !retainFailure) {
          latest.delete(field);
          failed.delete(field);
        } else {
          latest.set(field, Promise.resolve(false));
          failed.add(field);
        }
        return ok;
      });
      latest.set(field, recorded);
    },
    /**
     * Drops a settled failure. An in-flight save stays tracked so Share still
     * waits for the request the user already sent.
     */
    clearFailure(field: string): void {
      if (!failed.has(field)) return;
      failed.delete(field);
      latest.delete(field);
    },
    async wait(): Promise<boolean> {
      const seen = new Set<Promise<boolean>>();
      for (;;) {
        const pending = [...latest.values()].filter((promise) => !seen.has(promise));
        if (pending.length === 0) return latest.size === 0;
        for (const promise of pending) seen.add(promise);
        const results = await Promise.all(pending);
        if (results.some((ok) => !ok)) return false;
      }
    },
  };
}
